import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Check, ChevronLeft, ArrowRight, Activity, X, Clock, ShieldCheck, Landmark } from "lucide-react";
import SeoHead, { MAIN_SITE_URL } from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { FaqSection } from "./sections";
import { SmartLink, CalendlyLink } from "./SiteHeader";
import { STEPS, assess, GOAL_COPY, FLAG_COPY, VITALS, FAQS } from "./checkupData";

const API = process.env.REACT_APP_BACKEND_URL;
const PATH = "/tools/yearly-mortgage-checkup/";

function Option({ on, label, onClick, testId }) {
  return <button type="button" className={`ck-opt${on ? " on" : ""}`} onClick={onClick} data-testid={testId}><span className="ck-box">{on && <Check size={14} />}</span>{label}</button>;
}

function Wizard({ answers, setAnswers, step, setStep, onDone }) {
  const s = STEPS[step];
  const val = answers[s.id];
  const chosen = s.multi ? (val || []) : val;
  const ready = s.multi ? chosen.length > 0 : !!chosen;
  const pick = (k) => {
    if (!s.multi) return setAnswers({ ...answers, [s.id]: k });
    let next = chosen.includes(k) ? chosen.filter((x) => x !== k) : [...chosen, k];
    next = k === "none" ? ["none"] : next.filter((x) => x !== "none");
    setAnswers({ ...answers, [s.id]: next });
  };
  return (
    <div className="ck-card" data-testid="checkup-wizard">
      <div className="ck-progress"><span style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
      <div className="ck-stepno">Question {step + 1} of {STEPS.length}</div>
      <h3 className="ck-q">{s.q}</h3>
      <p className="ck-sub">{s.sub}</p>
      <div className="ck-opts">{s.opts.map(([k, label]) => <Option key={k} on={s.multi ? chosen.includes(k) : chosen === k} label={label} onClick={() => pick(k)} testId={`checkup-opt-${s.id}-${k}`} />)}</div>
      {s.rate && chosen && chosen !== "unsure" && (
        <label className="ck-rate">Your rate, if you know it <span>(optional)</span>
          <div className="ck-rate-in"><input type="number" step="0.01" min="0" max="15" inputMode="decimal" placeholder="e.g. 4.89" value={answers.rate || ""} onChange={(e) => setAnswers({ ...answers, rate: e.target.value })} data-testid="checkup-rate-input" /><b>%</b></div>
        </label>
      )}
      <div className="ck-nav">
        {step > 0 ? <button type="button" className="ck-back" onClick={() => setStep(step - 1)} data-testid="checkup-back"><ChevronLeft size={16} /> Back</button> : <span />}
        <button type="button" className="btn" disabled={!ready} onClick={() => (step < STEPS.length - 1 ? setStep(step + 1) : onDone())} data-testid="checkup-next">{step < STEPS.length - 1 ? "Next" : "Get my verdict"} <ArrowRight size={16} /></button>
      </div>
    </div>
  );
}

function Report({ answers, result, onRestart }) {
  const [c, setC] = useState({ name: "", email: "", phone: "" });
  const [sent, setSent] = useState(false);
  const [id, setId] = useState(null);
  const onTrack = result.verdict === "on_track";
  const goal = result.top ? GOAL_COPY[result.top] : null;
  const body = (extra) => JSON.stringify({ checkup_id: id, answers: { ...answers, rate: answers.rate ? Number(answers.rate) : null }, verdict: result.verdict, flags: result.flags, top_goal: result.top, ...extra });
  useEffect(() => {
    fetch(`${API}/api/public/checkup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: body({}) })
      .then((r) => (r.ok ? r.json() : null)).then((d) => d && setId(d.checkup_id)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (e) => {
    e.preventDefault();
    await fetch(`${API}/api/public/checkup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: body(c) }).catch(() => {});
    setSent(true);
  };
  return (
    <div className={`ck-card ck-report ${onTrack ? "ok" : "review"}`} data-testid="checkup-report">
      <div className="ck-verdict-tag"><Activity size={16} /> {onTrack ? "Verdict: you're good" : `Verdict: worth a call · ${result.flags.length} ${result.flags.length === 1 ? "thing" : "things"} flagged`}</div>
      {onTrack ? (
        <>
          <h3 className="ck-q">Good news. <em>You're winning.</em></h3>
          <p className="ck-sub">Nothing big has changed, your renewal isn't knocking, the payment fits and nothing's pulling at you. That's a mortgage doing its job — and we're not about to invent a reason to call you.</p>
          <ul className="ck-watch" data-testid="checkup-watch">
            <li><b>Know your renewal date.</b> 120 days out, every lender in Canada wants your business — for free. <SmartLink href="/tools/renewal-reminder/">Set a reminder →</SmartLink></li>
            <li><b>Glance at the board now and then.</b> If the best 5-year fixed drops half a point under your rate, come run this again. <SmartLink href="/rates">Today's rates →</SmartLink></li>
            <li><b>Live your life.</b> New job, new baby, new city — if any of those land, come back and we'll re-run it. <SmartLink href="/after-closing/">What we do after closing →</SmartLink></li>
          </ul>
        </>
      ) : (
        <>
          <h3 className="ck-q">{goal ? goal[0] : "There's money on the table."}</h3>
          <p className="ck-sub">{goal ? goal[1] : "Nothing here is an emergency. All of it is the kind of thing a 15-minute phone call turns into a plan."}</p>
          <div className="ck-flags" data-testid="checkup-flags">{result.flags.map((f) => <div className="ck-flag" key={f}><b>{FLAG_COPY[f][0]}</b><span>{FLAG_COPY[f][1]}</span></div>)}</div>
          {answers.rate && <p className="ck-bench">You told us <b>{Number(answers.rate).toFixed(2)}%</b>. Today's best 5-year fixed on our board is about <b>{result.bench.toFixed(2)}%</b>{result.flags.includes("rate_gap") ? " — that gap is worth weighing against your penalty." : " — you're in the neighbourhood. Nice."}</p>}
          <div className="ck-cta"><CalendlyLink className="btn" testId="checkup-book">Book the 15-minute call</CalendlyLink><span className="micro">Evenings and weekends are fine. Or text 780-900-2495 — so call us, maybe.</span></div>
        </>
      )}
      <form className="ck-save" onSubmit={save} data-testid="checkup-save-form">
        <div className="ck-save-head">{onTrack ? "Want us to keep an eye on things?" : "Rather we reach out?"} <span>Leave your details and Matt reads your answers before you ever talk.</span></div>
        {sent ? <p className="ck-sent" data-testid="checkup-saved">Got it. {onTrack ? "We'll give you a nudge before your renewal — and otherwise stay out of your way." : "Matt will have your answers in front of him when you connect. Talk soon."}</p> : (
          <div className="ck-save-grid">
            <input required placeholder="Name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="checkup-name" />
            <input required type="email" placeholder="Email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} data-testid="checkup-email" />
            <input placeholder="Phone (optional)" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} data-testid="checkup-phone" />
            <button className="btn outline" type="submit" data-testid="checkup-save">{onTrack ? "Keep me posted" : "Send Matt my answers"}</button>
          </div>
        )}
      </form>
      <button type="button" className="ck-back" onClick={onRestart} data-testid="checkup-restart">Start over</button>
    </div>
  );
}

function CheckupModal({ open, onClose, live }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const result = useMemo(() => (done ? assess(answers, live) : null), [done, answers, live]);
  const restart = () => { setAnswers({}); setStep(0); setDone(false); };
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="ck-modal" role="dialog" aria-modal="true" aria-label="Yearly Mortgage Checkup" data-testid="checkup-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ck-modal-inner">
        <button type="button" className="ck-close" onClick={onClose} aria-label="Close checkup" data-testid="checkup-close"><X size={20} /></button>
        {done && result ? <Report answers={answers} result={result} onRestart={restart} /> : <Wizard answers={answers} setAnswers={setAnswers} step={step} setStep={setStep} onDone={() => setDone(true)} />}
      </div>
    </div>
  );
}

const PROMISES = [
  [Clock, "Two minutes. Five questions.", "No documents, no account, no waiting for someone to phone you back."],
  [Landmark, "Any lender. Client or not.", "Big bank, credit union, one you've never heard of — same eight checks, same straight answer."],
  [ShieldCheck, "In your corner, not the bank's", "If you're on track, we tell you. The bank profits when you overpay. We don't."],
];

export default function CheckupPage() {
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/public/rates/5-yr-fixed`).then((r) => (r.ok ? r.json() : null)).then((d) => { const r = d?.featured?.rate ?? d?.lenders?.[0]?.rate; if (r) setLive(Number(r)); }).catch(() => {});
  }, []);
  const ld = { "@context": "https://schema.org", "@graph": [
    { "@type": "WebPage", name: "Yearly Mortgage Checkup | Hello Mortgage Team", url: `${MAIN_SITE_URL}${PATH}`, description: "A free two-minute yearly mortgage checkup for Alberta homeowners: rate, renewal, cash flow, equity, debts, payoff speed, flexibility and your next move." },
    { "@type": "HowTo", name: "Yearly mortgage checkup", step: VITALS.map((v, i) => ({ "@type": "HowToStep", position: i + 1, name: `${v.tag}: ${v.h}`, text: v.take })) },
    { "@type": "FAQPage", mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
  ] };
  const start = () => setOpen(true);
  return (
    <SiteLayout bodyClass="xp-page ck-page">
      <SeoHead title="Yearly Mortgage Checkup | Free 2-Minute Review for Alberta Homeowners | Hello Mortgage Team" description="Is your mortgage still the right fit? Free two-minute yearly mortgage checkup: rate, renewal, cash flow, equity, debts, payoff speed, flexibility and your next move — with an honest verdict, not a sales call." path={PATH} />
      <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>

      <section className="hero ck-hero" data-testid="checkup-hero">
        <div className="wrap hero-grid">
          <div>
            <h1 className="hero-eyebrow">Yearly Mortgage Checkup</h1>
            <h2 className="hero-title">Is your mortgage still <em>pulling its weight?</em></h2>
            <p>You've changed since you signed. Your mortgage hasn't noticed. Five quick questions, two minutes, and you'll know whether it's still winning for you — or whether there's money on the table. If you're good, we'll say so and leave you alone.</p>
            <div className="actions">
              <button className="btn" onClick={start} data-testid="checkup-start">Start my checkup</button>
              <a href="#checks" className="textlink" data-testid="checkup-learn">What we look at (all eight)</a>
            </div>
            <div className="micro">Free · Any lender · Two minutes · Nobody calls unless there's a reason to</div>
          </div>
          <img src="/brand/checkup-mascot.png" alt="Hello Mortgage mascot in a doctor's coat holding a checkup clipboard" className="ck-mascot" />
        </div>
      </section>

      <section className="ck-promise" data-testid="checkup-promise">
        <div className="wrap ck-promise-grid">
          {PROMISES.map(([Icon, h, p]) => <div className="ck-promise-item" key={h}><Icon size={26} strokeWidth={1.8} /><div><b>{h}</b><span>{p}</span></div></div>)}
        </div>
      </section>

      <section className="ck-checks" id="checks" data-testid="checkup-checks">
        <div className="wrap">
          <div className="intro"><div><div className="eyebrow">What we look at</div><h2 className="display">Eight places a mortgage <em>quietly leaks money.</em></h2></div><p>None of them show up on your statement. All of them show up in your bank account, eventually. Here's each one, what to ask yourself, and the real Alberta math — so you can see where you stand before we ever talk.</p></div>
          <ol className="ck-list">
            {VITALS.map((v, i) => (
              <li className={`ck-check${i % 2 ? " flip" : ""}`} key={v.id} id={`check-${v.id}`} data-testid={`vital-${v.id}`}>
                <div className="ck-check-media"><img src={v.img} alt={v.h} loading="lazy" /><span className="ck-check-no">{String(i + 1).padStart(2, "0")}</span></div>
                <div className="ck-check-body">
                  <div className="eyebrow">{v.tag}</div>
                  <h3>{v.h}</h3>
                  <p className="ck-check-intro">{v.intro}</p>
                  <div className="ck-asks-label">Worth asking</div>
                  <ul className="ck-asks">{v.asks.map((a) => <li key={a}>{a}</li>)}</ul>
                  <blockquote className="ck-take"><span className="eyebrow">Here's the play</span>{v.take}</blockquote>
                  <SmartLink href={v.link[1]} className="ck-check-link" data-testid={`vital-link-${v.id}`}>{v.link[0]} →</SmartLink>
                </div>
              </li>
            ))}
          </ol>
          <div className="ck-mid-cta"><p className="display">Eight leaks. Two minutes. One straight answer.</p><button className="btn" onClick={start} data-testid="checkup-mid-cta">Start my checkup</button></div>
        </div>
      </section>

      <section className="ck-compare" data-testid="checkup-compare">
        <div className="wrap">
          <div className="intro"><div><div className="eyebrow">Why this one's different</div><h2 className="display">You should leave with <em>an answer,</em> not a voicemail.</h2></div><p>Most "checkups" are a contact form wearing a lab coat. You fill it in, someone phones you, and the pitch starts. Ours hands you the verdict first — then decides whether a call is even worth your time.</p></div>
          <div className="ck-table" role="table">
            <div className="ck-tr ck-th" role="row"><span /><span>The usual "checkup"</span><span className="hl">This one</span></div>
            {[["What you get on the spot", "A thank-you screen", "Your verdict, what's flagged, and the math behind it"], ["If nothing's changed", "Someone calls anyway", "\"You're good\" — and we mean it"], ["Who it's for", "Mostly their own clients", "Any Alberta homeowner, any lender"], ["Your rate vs. today's", "Later, over the phone", "Right now, against our live board"], ["The catch", "The pitch", "There isn't one. We're paid by the lender, not you"]].map(([k, a, b]) => (
              <div className="ck-tr" role="row" key={k}><span className="ck-td-k">{k}</span><span>{a}</span><span className="hl"><Check size={16} /> {b}</span></div>
            ))}
          </div>
        </div>
      </section>

      <div id="faq"><FaqSection items={FAQS} eyebrow="Checkup questions" heading="The stuff people ask before they hit start." /></div>

      <section className="final" id="talk"><div className="wrap"><p className="display final-title">Two minutes now. Zero surprises later.</p><p>Find out if your mortgage is still winning for you. If it is, great — go enjoy your weekend. If it isn't, you'll know exactly where the money's hiding, and we'll go get it together.</p><button className="btn" onClick={start} data-testid="checkup-final-cta">Start my checkup</button></div></section>

      <CheckupModal open={open} onClose={() => setOpen(false)} live={live} />
    </SiteLayout>
  );
}
